import React, { useState } from "react"
import styled, { css } from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"
import PropTypes from "prop-types"

import ImageContentful from "./ImageContentful"
import Link from "./Link"

import { media, mixins } from "../styles"
import { slugify } from "../helpers"

import ArrowRightSVG from "../images/icons/arrow-right.inline.svg"

// export const CardGrid = styled.div`
//   display: flex;
//   flex-flow: column wrap;
//   gap: 20px;

//   @media ${media.sm} {
//     flex-direction: row;
//   }
// `

const Content = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;
`

const CardHeaderArrow = styled.span`
  color: var(--color-sun);
  display: inline-block;
  height: 12px;
  margin: 0 0 0 5px;
  transition: opacity var(--transitionTime);
  vertical-align: -1px;
  width: 12px;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media ${media.xl} {
    vertical-align: 0;
  }

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`

const CardHeaderOverride = styled.span`
  display: inline-block;
`

const BackgroundImage = styled.div`
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity var(--transitionTime);
  user-select: none;
  z-index: 0;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  .gatsby-image-wrapper {
    min-height: 100%;
  }
`

const Description = styled.div`
  margin: 0 0 30px;

  p {
    ${mixins.copy5}
  }
`

export const CardHeader = styled.h3`
  ${mixins.title5}

  @media ${media.lg} {
    min-height: 40px;
  }

  @media ${media.xl} {
    min-height: 60px;
  }
`

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const LinkListItem = styled.li`
  margin: 0 0 20px;

  @media ${media.lg} {
    margin: 0 0 30px;
  }
`

const LinkIcon = styled.span`
  color: var(--color-spaceblue);
  display: block;
  height: 24px;
  margin: 0 5px 0 0;
  transition: color var(--transitionTime);
  width: 24px;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  svg {
    height: auto;
    width: 100%;
  }
`

const LinkItem = styled(Link)`
  text-decoration: none;

  > span {
    align-items: center;
    display: flex;
  }

  &:hover,
  &:focus {
    ${LinkIcon} {
      color: var(--color-sun);
    }
  }
`

const LinkText = styled.span`
  align-items: center;
  ${mixins.copy5}
`

const LinkArrow = styled.span`
  display: inline-block;
  height: 12px;
  margin: 0 0 0 5px;
  vertical-align: -1px;
  width: 12px;

  @media ${media.xl} {
    vertical-align: 0;
  }

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`

const ShowContent = styled.div`
  opacity: 0;
  transition:
    opacity var(--transitionTime),
    transform var(--transitionTime);
  transform: translate3d(0, 0, 0);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const Card = styled.div`
  background: var(--color-greyLight);
  border-radius: 25px;
  color: var(--color-spaceblue);
  cursor: default;
  display: flex;
  margin: 0 0 20px;
  min-height: 385px;
  overflow: hidden;
  padding: 30px;
  position: relative;
  text-decoration: none;
  transform: translateZ(0);

  @media ${media.sm} {
    height: calc(100% - 20px);
    min-height: 410px;
  }

  @media ${media.md} {
    padding: 35px;
  }

  @media ${media.lg} {
    min-height: 385px;
  }

  @media ${media.xl} {
    min-height: 485px;
  }

  ${(props) =>
    !props.$isLink &&
    css`
      &:hover,
      &:focus,
      &[aria-expanded="true"] {
        ${BackgroundImage} {
          opacity: 0;
        }

        ${CardHeaderArrow} {
          opacity: 0;
        }

        ${ShowContent} {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
    `}

  ${(props) =>
    props.$isLink &&
    css`
      cursor: pointer;

      &:hover,
      &:focus,
      &:focus-within {
        ${BackgroundImage} {
          opacity: 0.5;
        }

        ${ShowContent} {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
    `}
`

const CategoryCard = ({ category, className, headerLevel = 2 }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const isLinkCard = Boolean(category.to) // Linked cards are navigation links, non-linked cards are expandable
  const id = `category-card-${slugify(category.name)}`
  const ariaDescription = category.description

  return (
    <Card
      as={category.to ? Link : "div"}
      aria-describedby={ariaDescription ? `${id}-desc` : undefined}
      aria-expanded={isLinkCard ? undefined : isExpanded}
      aria-label={category.name}
      className={className}
      $isLink={category.to ? 1 : 0}
      onBlur={() => setIsExpanded(false)}
      onFocus={() => setIsExpanded(true)}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      tabIndex={0}
      to={category.to}
    >
      <Content>
        <CardHeader id={`${id}-title`} as={`h${headerLevel}`}>
          {category.name}
          {category.overrideArrow ? (
            <CardHeaderOverride aria-hidden="true">
              {category.overrideArrow}
            </CardHeaderOverride>
          ) : (
            <CardHeaderArrow aria-hidden="true">
              <ArrowRightSVG />
            </CardHeaderArrow>
          )}
        </CardHeader>
        <ShowContent>
          <Description id={ariaDescription ? `${id}-desc` : undefined}>
            {category.description && <p>{category.description}</p>}
          </Description>
          {category.links?.length > 0 && (
            <LinkList
              role="navigation"
              aria-label={`${category.name} services`}
            >
              {category.links.map((link, i) => {
                return (
                  <LinkListItem key={i}>
                    <LinkItem to={link.to}>
                      <LinkIcon aria-hidden="true">
                        <ImageContentful alt="" image={link.icon} inline />
                      </LinkIcon>
                      <LinkText>
                        {link.name}
                        <LinkArrow aria-hidden="true">
                          <ArrowRightSVG />
                        </LinkArrow>
                      </LinkText>
                    </LinkItem>
                  </LinkListItem>
                )
              })}
            </LinkList>
          )}
        </ShowContent>
      </Content>
      {category.image ? (
        <BackgroundImage aria-hidden="true">
          <GatsbyImage alt="" image={category.image.gatsbyImageData} />
        </BackgroundImage>
      ) : null}
    </Card>
  )
}

CategoryCard.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string, // TODO: should be required but it's tripping up
  headerLevel: PropTypes.oneOf([2, 3, 4]),
  image: PropTypes.object,
  arrowOverride: PropTypes.node,
  to: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.object,
      to: PropTypes.string.isRequired,
    })
  ),
}

export default CategoryCard
